import Breadcrumbs from "@/components/Breadcrumbs";
import {
    CommentsService,
    ElementDatabaseService,
    PageService,
    VersionManagementService,
} from "@/services/admin-services";
import { DatabaseElement, ElementCommentItem, Page, Version } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Discipline, Software, UseCase } from "@/services/global-services/models";
import { PageType } from "@/services/shared-models";
import { Dictionary } from "@/utils/Dictionary";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import {
    alpha,
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import { parseJSON } from "date-fns";
import { groupBy, orderBy } from "lodash";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ElementInfo } from "../elements-overview/Overview";

const breadcrumbs = [
    {
        to: "../..",
        title: "Administration",
    },
    {
        title: "Element Comments",
        to: "..",
    },
];

interface UserSettings {
    disciplines: Array<string>;
    software: Array<string>;
    useCases: Array<string>;
}

const Details = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [breadcrumbTrail, setBreadcrumbTrail] = useState(breadcrumbs);
    const [comments, setComments] = useState<Array<ElementCommentItem>>([]);
    const [isUpdating, setIsUpdating] = useState<string>();

    const { identifier, language } = useParams();
    const theme = useTheme();

    const elementService = useMemo(() => new ElementDatabaseService("int", "int-fr"), []);
    const commentsService = useMemo(() => new CommentsService("int"), []);
    const pagesService = useMemo(() => new PageService("int"), []);
    const regionService = useMemo(() => new LocaleService("int", "en"), []);
    const versionService = useMemo(() => new VersionManagementService(), []);

    const [versions, setVersions] = useState<Array<Version>>([]);
    const [isLoadingSettings, setIsLoadingSettings] = useState<boolean>(true);

    const disciplines = useRef<Dictionary<Array<Discipline>>>({});
    const software = useRef<Dictionary<Array<Software>>>({});
    const useCases = useRef<Dictionary<Array<UseCase>>>({});

    const elementPageSlugs = useRef<Dictionary<string>>({});
    const elementSlugs = useRef<Dictionary<ElementInfo>>({});

    useEffect(() => {
        Promise.all([
            commentsService.getForElement(identifier!, language!).then(setComments),
            pagesService.getPages().then(handlePages),
            elementService.get().then(handleElements),
            versionService.get().then(setVersions),
            // Get mapping for disciplines, software and use cases
        ]).then(() => setIsLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (comments.length === 0 || versions.length === 0) return;

        const detectedVersions = groupBy(comments, (c) => c.bidsVersionId);
        const versionInformation: Promise<void>[] = [];

        for (const versionId in detectedVersions) {
            const version = versions.find((x) => x.id === versionId);
            if (!version) continue;

            versionInformation.push(
                regionService.getDisciplines(version.name).then((disciplines) => {
                    setDisciplines(version.name, disciplines);
                })
            );
            versionInformation.push(
                regionService.getSoftware(version.name).then((software) => {
                    setSoftware(version.name, software);
                })
            );
            versionInformation.push(
                regionService.getUseCases(version.name).then((useCases) => {
                    setUseCases(version.name, useCases);
                })
            );
        }

        Promise.all(versionInformation).then(() => setIsLoadingSettings(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comments, versions]);

    const setDisciplines = (version: string, data: Array<Discipline>) => (disciplines.current[version] = data);
    const setSoftware = (version: string, data: Array<Software>) => (software.current[version] = data);
    const setUseCases = (version: string, data: Array<UseCase>) => (useCases.current[version] = data);

    const handlePages = (pages: Array<Page>) => {
        const elementPages = pages.filter((x) => x.pageType === PageType.Elements);
        elementPages.forEach((page) => {
            elementPageSlugs.current[page.language] = page.slug;
        });
    };

    const handleElements = (elements: Array<DatabaseElement>) => {
        elements.forEach((element) => {
            elementSlugs.current[element.id] = { slug: element.slug, name: element.name };
        });

        setBreadcrumbTrail([...breadcrumbs, { title: `${elementSlugs.current[identifier!]?.name}`, to: "" }]);
    };

    const renderStatus = (read: boolean) => {
        if (read) return null;

        const statusColor = theme.palette.warning;

        return (
            <Typography
                variant={"caption"}
                fontWeight={700}
                sx={{
                    bgcolor: alpha(statusColor.light, 0.1),
                    color: statusColor.dark,
                    paddingX: 1.5,
                    paddingY: 0.5,
                    borderRadius: 4,
                    display: "inline",
                }}
            >
                New
            </Typography>
        );
    };

    const renderVersion = (versionId: string) => {
        const version = versions.find((x) => x.id === versionId);
        if (!version) return "-";

        return version.name;
    };

    const onUpdateStatus = (id: string, parentId?: string) => {
        setIsUpdating(`${id}-Read`);

        commentsService.markAsRead(id).then(() => {
            setIsUpdating("");

            const updatedComments = [...comments];
            let updateComment: ElementCommentItem;
            if (parentId) {
                const parentIndex = comments.findIndex((c) => c.id === parentId);
                const parent = comments[parentIndex];
                const childIndex = parent.replies.findIndex((c) => c.id === id);
                updateComment = parent.replies[childIndex];
                updateComment.isRead = true;
                updatedComments[parentIndex].replies[childIndex] = updateComment;
            } else {
                const commentIndex = comments.findIndex((c) => c.id === id);
                updateComment = comments[commentIndex];
                updateComment.isRead = true;
                updatedComments[commentIndex] = updateComment;
            }

            setComments(updatedComments);
        });
    };

    const onDelete = (id: string, parentId?: string) => {
        setIsUpdating(`${id}-Delete`);

        commentsService.delete(id).then(() => {
            setIsUpdating("");

            const updatedComments = [...comments];
            if (parentId) {
                const parentIndex = comments.findIndex((c) => c.id === parentId);
                const parent = comments[parentIndex];
                const childIndex = parent.replies.findIndex((c) => c.id === id);
                updatedComments[parentIndex].replies.splice(childIndex, 1);
            } else {
                const commentIndex = comments.findIndex((c) => c.id === id);
                updatedComments.splice(commentIndex, 1);
            }

            setComments(updatedComments);
        });
    };

    const renderUserSettings = (item: ElementCommentItem) => {
        let userSettings: UserSettings | null;
        try {
            userSettings = JSON.parse(item.uiBlob);
        } catch {
            userSettings = null;
        }

        if (isLoadingSettings)
            return (
                <TableRow>
                    <TableCell
                        component="td"
                        scope="row"
                        colSpan={4}
                        sx={{
                            height: 100,
                            alignItems: "center",
                            flexDirection: "column",
                        }}
                    >
                        <Box sx={{ display: "flex", placeContent: "center" }}>
                            <CircularProgress />
                        </Box>
                    </TableCell>
                </TableRow>
            );

        if (!userSettings) {
            return (
                <TableRow>
                    <TableCell
                        component="td"
                        scope="row"
                        colSpan={4}
                        sx={{
                            alignItems: "center",
                            flexDirection: "column",
                        }}
                    >
                        <Typography color={"text.secondary"} variant={"overline"}>
                            No additional comment information stored.
                        </Typography>
                    </TableCell>
                </TableRow>
            );
        }

        const version = versions.find((x) => x.id === item.bidsVersionId);
        if (!version) return null;

        const disciplineList = userSettings.disciplines.map(
            (d) => disciplines.current[version.name].find((x) => x.id === d)?.label
        );
        const softwareList = userSettings.software.map(
            (s) => software.current[version.name].find((x) => x.id === s)?.name
        );
        const useCaseList = userSettings.useCases.map(
            (u) => useCases.current[version.name].find((x) => x.id === u)?.name
        );

        return (
            <TableRow>
                <TableCell component="td" scope="row" colSpan={4}>
                    <Box>
                        <Typography color={"text.secondary"} variant={"overline"}>
                            Disciplines:
                        </Typography>
                        <Typography color={"text.secondary"} variant={"body2"} sx={{ ml: 1 }}>
                            {disciplineList.join(", ")}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography color={"text.secondary"} variant={"overline"}>
                            Software:
                        </Typography>
                        <Typography color={"text.secondary"} variant={"body2"} sx={{ ml: 1 }}>
                            {softwareList.join(", ")}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography color={"text.secondary"} variant={"overline"}>
                            Use-cases:
                        </Typography>
                        <Typography color={"text.secondary"} variant={"body2"} sx={{ ml: 1 }}>
                            {useCaseList.join(", ")}
                        </Typography>
                    </Box>
                </TableCell>
            </TableRow>
        );
    };

    const renderComment = (key: number, item: ElementCommentItem, parentId?: string) => {
        return (
            <Fragment key={key}>
                <TableRow
                    key={key}
                    sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        verticalAlign: "top",
                    }}
                >
                    <TableCell
                        component="th"
                        scope="row"
                        rowSpan={2}
                        sx={{
                            background: item.isAdmin
                                ? "linear-gradient(90deg, #05B692 10px, transparent 0%);"
                                : "unset",
                        }}
                    >
                        <Typography
                            color={"text.secondary"}
                            variant={"body2"}
                            sx={{ whiteSpace: "pre-wrap", ml: parentId ? 4 : "unset" }}
                        >
                            {item.commentBlob}
                        </Typography>
                    </TableCell>
                    <TableCell component="td" scope="row">
                        <Typography color={"text.secondary"} variant={"body2"}>
                            {item.username} ({item.email})
                        </Typography>
                    </TableCell>
                    <TableCell component="td" scope="row">
                        <Typography color={"text.secondary"} variant={"body2"}>
                            {parseJSON(item.commentDate).toLocaleString("nl-be") || "-"} {renderStatus(item.isRead)}
                        </Typography>
                    </TableCell>
                    <TableCell component="td" scope="row">
                        <Typography color={"text.secondary"} variant={"body2"}>
                            {renderVersion(item.bidsVersionId)}
                        </Typography>
                    </TableCell>
                    <TableCell scope="row" sx={{ p: 0 }}>
                        <Box sx={{ display: "flex", m: 0.5 }}>
                            <Box sx={{ ml: "auto", mr: 1, position: "relative" }}>
                                <Button
                                    color={"primary"}
                                    onClick={() => onUpdateStatus(item.id, parentId)}
                                    disabled={Boolean(isUpdating) || item.isRead}
                                    sx={{ whiteSpace: "nowrap" }}
                                >
                                    Mark as read
                                </Button>
                                {isUpdating === `${item.id}-Read` && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                        }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ mr: 1, position: "relative" }}>
                                <Button
                                    color={"error"}
                                    onClick={() => onDelete(item.id, parentId)}
                                    disabled={Boolean(isUpdating)}
                                >
                                    Delete
                                </Button>
                                {isUpdating === `${item.id}-Delete` && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                        }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ ml: "auto", position: "relative" }}>
                                <Button
                                    color={"info"}
                                    onClick={() => onUpdateStatus(item.id, parentId)}
                                    sx={{ whiteSpace: "nowrap" }}
                                    href={`/${item.languageCode.replace("int", "be")}/${
                                        elementPageSlugs.current["int-en"]
                                    }/${elementSlugs.current[item.baseElementId]?.slug}`}
                                    target={"_blank"}
                                >
                                    View
                                </Button>
                            </Box>
                        </Box>
                    </TableCell>
                </TableRow>
                {renderUserSettings(item)}

                {item.replies?.map((reply, i) => renderComment(i, reply, item.id))}
            </Fragment>
        );
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbTrail} />

            <Divider />

            <Grid container spacing={4} p={4}>
                <Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex", flexDirection: "column" }}>
                    <Box sx={{ ml: "auto", position: "relative" }}>
                        <Button
                            variant="contained"
                            component="a"
                            startIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
                            href={`/be-en/${elementPageSlugs.current["int-en"]}/${
                                elementSlugs.current[comments[0]?.baseElementId]?.slug
                            }`}
                            target="_blank"
                            disabled={isLoading}
                        >
                            View element page
                        </Button>
                        {isLoading && (
                            <CircularProgress
                                size={24}
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    marginTop: "-12px",
                                    marginLeft: "-12px",
                                }}
                            />
                        )}
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell sx={{ minWidth: 500 }}>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Comment
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Posted by
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Posted on
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Version
                                        </Typography>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading || !comments ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : comments.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                height: 200,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ textAlign: "center" }}>
                                                No comments available for this element.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    orderBy(comments, "commentDate", "desc").map((item, i) => renderComment(i, item))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Details;
